import React, { useState, useEffect } from "react";
import { SeuilAlerte, Indicateur, Alerte } from "../types/Indicateur";
import BackButton from "./BackButton";
import "../styles/SystemeAlerte.css";
import { useAuth } from "../context/AuthContext";
import { sapService } from "../services/SapService";
import { Sap } from "../types/Sap";
import { FaInfinity } from "react-icons/fa";
import { IconType } from "react-icons";
import { useNavigate } from "react-router-dom";

type CanalType = "sms" | "whatsapp" | "email";
const couleurs: SeuilAlerte["couleur"][] = ["rouge", "orange", "jaune", "vert", "bleu"];

const colorMap: Record<string, string> = {
  rouge: "#e74c3c",
  orange: "#e67e22",
  jaune: "#f1c40f",
  vert: "#2ecc71",
  bleu: "#3498db",
};

interface Props {
  indicateurs: Indicateur[];
  initialIndicateurId?: number;
  onBack?: () => void;
}

interface SeuilAlerteMinMax {
  couleur: SeuilAlerte["couleur"];
  minValue: number | null;
  maxValue: number | null;
  useMin: boolean;
  useMax: boolean;
  message: string;
  canal: string[];
  destinataires: {
    sms: string[];
    whatsapp: string[];
    email: string[];
  };
}

const SafeIcon: React.FC<{ icon?: IconType; size?: number; className?: string }> = ({ icon, size, className }) => {
  if (!icon) return null;
  const IconComp = icon as React.ElementType;
  return <IconComp size={size} className={className} />;
};

const seuilIsEmpty = (s: SeuilAlerteMinMax) => {
  const noMessage = !s.message || s.message.trim() === "";
  const noValues = (!s.useMin || s.minValue === null) && (!s.useMax || s.maxValue === null);
  const noDest =
    s.destinataires.sms.length === 0 &&
    s.destinataires.whatsapp.length === 0 &&
    s.destinataires.email.length === 0;
  return noMessage && noValues && noDest;
};

const checkOverlap = (seuils: SeuilAlerteMinMax[]): string | null => {
  const activeSeuils = seuils.filter((s) => !seuilIsEmpty(s));

  for (let i = 0; i < activeSeuils.length; i++) {
    for (let j = i + 1; j < activeSeuils.length; j++) {
      const s1 = activeSeuils[i];
      const s2 = activeSeuils[j];

      const min1 = (s1.useMin ? -Infinity : (s1.minValue ?? -Infinity)) as number;
      const max1 = (s1.useMax ? Infinity : (s1.maxValue ?? Infinity)) as number;
      const min2 = (s2.useMin ? -Infinity : (s2.minValue ?? -Infinity)) as number;
      const max2 = (s2.useMax ? Infinity : (s2.maxValue ?? Infinity)) as number;

      // Vérifier si les intervalles se chevauchent
      const overlap = !(max1 < min2 || max2 < min1);
      
      if (overlap) {
        return `Conflit détecté entre les seuils "${s1.couleur}" et "${s2.couleur}". Les plages de valeurs ne peuvent pas se chevaucher.`;
      }
    }
  }

  return null;
};

const SystemeAlerte: React.FC<Props> = ({ indicateurs, initialIndicateurId }) => {
  const { api } = useAuth();
  const [selectedIndicateurId] = useState<number | null>(initialIndicateurId ?? null);
  const [sapId, setSapId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const [seuils, setSeuils] = useState<SeuilAlerteMinMax[]>(
    couleurs.map((c) => ({
      couleur: c,
      minValue: null,
      maxValue: null,
      useMin: false,
      useMax: false,
      message: "",
      canal: [],
      destinataires: { sms: [], whatsapp: [], email: [] },
    }))
  );

  // Charger les données depuis l'API au chargement
  useEffect(() => {
    const fetchSapData = async () => {
      if (!selectedIndicateurId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const saps = await sapService.getSapByIdIndicateur(api, selectedIndicateurId!);

        if (saps) {
          const sapsArray = Array.isArray(saps) ? saps : [saps];
          
          // Valeurs extrêmes pour représenter l'infini
          const MIN_INFINITY = -999999999;
          const MAX_INFINITY = 999999999;
          
          const seuilsFromSap = couleurs.map((c) => {
            const sapForColor = sapsArray.find((s: any) => 
              s.nom && s.nom.toLowerCase() === c.toLowerCase()
            );

            if (sapForColor) {
              const sapData = sapForColor as any;
              if (sapData.id && !sapId) {
                setSapId(sapData.id);
              }

              const destSms = sapForColor.alertes?.filter((a: any) => a.type === "sms").map((a: any) => a.value_) || [];
              const destWhatsapp = sapForColor.alertes?.filter((a: any) => a.type === "whatsapp").map((a: any) => a.value_) || [];
              const destEmail = sapForColor.alertes?.filter((a: any) => a.type === "email").map((a: any) => a.value_) || [];

              // Détecter si c'est l'infini en vérifiant les valeurs extrêmes
              const isMinInfinity = sapForColor.minValue <= MIN_INFINITY;
              const isMaxInfinity = sapForColor.maxValue >= MAX_INFINITY;

              return {
                couleur: c,
                minValue: isMinInfinity ? null : sapForColor.minValue,
                maxValue: isMaxInfinity ? null : sapForColor.maxValue,
                useMin: isMinInfinity,
                useMax: isMaxInfinity,
                message: sapForColor.message || "",
                canal: [],
                destinataires: {
                  sms: destSms,
                  whatsapp: destWhatsapp,
                  email: destEmail,
                },
              };
            }

            return {
              couleur: c,
              minValue: null,
              maxValue: null,
              useMin: false,
              useMax: false,
              message: "",
              canal: [],
              destinataires: {
                sms: [],
                whatsapp: [],
                email: [],
              },
            };
          });

          setSeuils(seuilsFromSap);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération du SAP :", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSapData();
  }, [api, selectedIndicateurId]);

  // --- POPUPS ---
  const [popupVisible, setPopupVisible] = useState(false);
  const [currentCouleur, setCurrentCouleur] = useState<string | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<CanalType>("sms");
  const [newDest, setNewDest] = useState("");

  const [popupViewVisible, setPopupViewVisible] = useState(false);
  const [colorForView, setColorForView] = useState<string | null>(null);

  const openPopup = (couleur: string) => {
    setCurrentCouleur(couleur);
    setNewDest("");
    setSelectedChannel("sms");
    setPopupVisible(true);
  };

  const openViewPopup = (couleur: string) => {
    setColorForView(couleur);
    setPopupViewVisible(true);
  };

  const addDestinataire = () => {
    if (!newDest.trim() || !currentCouleur) return;

    setSeuils((prev) =>
      prev.map((s) =>
        s.couleur === currentCouleur
          ? {
              ...s,
              destinataires: {
                ...s.destinataires,
                [selectedChannel]: [...s.destinataires[selectedChannel], newDest.trim()],
              },
            }
          : s
      )
    );

    setNewDest("");
  };

  const removeDest = (couleur: string, canal: CanalType, index: number) => {
    setSeuils((prev) =>
      prev.map((s) =>
        s.couleur === couleur
          ? {
              ...s,
              destinataires: {
                ...s.destinataires,
                [canal]: s.destinataires[canal].filter((_, i) => i !== index),
              },
            }
          : s
      )
    );
  };

  const handleChangeSeuil = (couleur: string, field: keyof SeuilAlerteMinMax, value: any) => {
    setSeuils((prev) => prev.map((s) => (s.couleur === couleur ? { ...s, [field]: value } : s)));
  };

  const handleSave = async () => {
    if (!selectedIndicateurId) {
      alert("Veuillez sélectionner un indicateur");
      return;
    }

    try {
      const seuilsFiltres = seuils.filter((s) => !seuilIsEmpty(s));

      if (seuilsFiltres.length === 0) {
        alert("Veuillez configurer au moins un seuil avant d'enregistrer");
        return;
      }

      // Vérifier les chevauchements
      const overlapError = checkOverlap(seuils);
      if (overlapError) {
        alert(overlapError);
        return;
      }

      // Vérifier que chaque seuil a au moins min ou max
      for (const seuil of seuilsFiltres) {
        if (seuil.useMin && seuil.useMax) {
          // Les deux sont à l'infini, c'est valide mais vérifier qu'il y a un message
          continue;
        }

        if (!seuil.useMin && !seuil.useMax) {
          // Aucun infini, donc doit avoir les deux valeurs
          if (seuil.minValue === null || seuil.maxValue === null) {
            alert(`Le seuil "${seuil.couleur}" doit avoir des valeurs min et max, ou cocher les cases infini`);
            return;
          }
          
          if (seuil.minValue >= seuil.maxValue) {
            alert(`Le seuil "${seuil.couleur}" : la valeur min doit être inférieure à la valeur max`);
            return;
          }
        }

        if (seuil.useMin && !seuil.useMax && seuil.maxValue === null) {
          alert(`Le seuil "${seuil.couleur}" doit avoir une valeur max ou cocher l'infini positif`);
          return;
        }

        if (!seuil.useMin && seuil.useMax && seuil.minValue === null) {
          alert(`Le seuil "${seuil.couleur}" doit avoir une valeur min ou cocher l'infini négatif`);
          return;
        }
      }

      // Créer un SAP pour chaque couleur/seuil
      for (const seuil of seuilsFiltres) {
        const alertes: Array<{ value_: string; type: "sms" | "whatsapp" | "email"; idSap: number }> = [];
        
        seuil.destinataires.sms.forEach((dest) => {
          alertes.push({ value_: dest, type: "sms" as const, idSap: 0 });
        });
        seuil.destinataires.whatsapp.forEach((dest) => {
          alertes.push({ value_: dest, type: "whatsapp" as const, idSap: 0 });
        });
        seuil.destinataires.email.forEach((dest) => {
          alertes.push({ value_: dest, type: "email" as const, idSap: 0 });
        });

        // Utiliser des valeurs extrêmes pour représenter l'infini au lieu de null
        const MIN_INFINITY = -999999999;
        const MAX_INFINITY = 999999999;

        const newSap: Sap = {
          nom: seuil.couleur,
          minValue: seuil.useMin ? MIN_INFINITY : (seuil.minValue ?? 0),
          maxValue: seuil.useMax ? MAX_INFINITY : (seuil.maxValue ?? 0),
          message: seuil.message || "",
          createdAt: new Date().toISOString(),
          idIndicateur: selectedIndicateurId!,
          alertes: alertes.length > 0 ? alertes : undefined,
        };

        try {
          const response = await sapService.createSap(api, newSap);
        } catch (err) {
          console.error(`Erreur pour ${seuil.couleur}:`, err);
          throw err;
        }
      }
      
      alert("Alertes enregistrées avec succès !");
      navigate("/admin");
    } catch (error: any) {
      console.error("Erreur lors de l'enregistrement :", error);
      const errorMsg = error.response?.data?.message || error.message || "Erreur inconnue";
      alert(`Erreur lors de l'enregistrement des alertes: ${errorMsg}`);
    }
  };

  if (loading) {
    return (
      <div className="sa-container">
        <div className="sa-card">
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sa-container">
      <div className="sa-card">
        <BackButton text="Retour" />

        <h2 className="sa-title">
          {selectedIndicateurId
            ? `Configurer les alertes pour : "${
                indicateurs.find((ind) => ind.id === selectedIndicateurId)?.nom ?? ""
              }"`
            : "Système d'Alerte Précoce"}
        </h2>

        {seuils.map((s) => (
          <div key={s.couleur} className="sa-seuil">
            <div className="sa-color-label">
              <span className="sa-color-dot" style={{ backgroundColor: colorMap[s.couleur] }} />
              <strong>{s.couleur}</strong>
            </div>

            <div className="sa-row">
              <input
                type="number"
                value={s.minValue ?? ""}
                onChange={(e) => handleChangeSeuil(s.couleur, "minValue", e.target.value ? Number(e.target.value) : null)}
                className="sa-input"
                disabled={s.useMin}
                placeholder="Min"
              />

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '8px' }}>
                <input
                  type="checkbox"
                  checked={s.useMin}
                  onChange={(e) => {
                    handleChangeSeuil(s.couleur, "useMin", e.target.checked);
                    if (e.target.checked) {
                      handleChangeSeuil(s.couleur, "minValue", null);
                    }
                  }}
                />
                <span title="Valeur minimale" style={{ color: "#c00" }}>                  
                  - <SafeIcon icon={FaInfinity} size={16} />
                </span>
              </label>

              <span style={{ margin: '0 12px' }}>à</span>

              <input
                type="number"
                value={s.maxValue ?? ""}
                onChange={(e) => handleChangeSeuil(s.couleur, "maxValue", e.target.value ? Number(e.target.value) : null)}
                className="sa-input"
                disabled={s.useMax}
                placeholder="Max"
              />

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '8px' }}>
                <input
                  type="checkbox"
                  checked={s.useMax}
                  onChange={(e) => {
                    handleChangeSeuil(s.couleur, "useMax", e.target.checked);
                    if (e.target.checked) {
                      handleChangeSeuil(s.couleur, "maxValue", null);
                    }
                  }}
                />
                <span title="Valeur maximale" style={{ color: "#198754" }}>                  
                  + <SafeIcon icon={FaInfinity} size={16} />
                </span>
              </label>
            </div>

            <div className="sa-info-text" style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
              {s.useMin && s.useMax && "De l'infini négatif à l'infini positif (toutes les valeurs)"}
              {s.useMin && !s.useMax && s.maxValue !== null && "De l'infini négatif jusqu'à " + s.maxValue}
              {!s.useMin && s.useMax && s.minValue !== null && "De " + s.minValue + " jusqu'à l'infini positif"}
              {!s.useMin && !s.useMax && s.minValue !== null && s.maxValue !== null && "Entre " + s.minValue + " et " + s.maxValue}
              {!s.useMin && !s.useMax && (s.minValue === null || s.maxValue === null) && "Définissez les valeurs min et max ou cochez les cases infini"}
            </div>

            <div className="sa-message-block">
              <label>Message :</label>
              <textarea
                value={s.message}
                onChange={(e) => handleChangeSeuil(s.couleur, "message", e.target.value)}
                className="sa-textarea"
              />
            </div>

            <div className="sa-actions">
              <button className="btn-add btn-add-green" onClick={() => openPopup(s.couleur)}>
                + Ajouter
              </button>
              <button className="btn-view" onClick={() => openViewPopup(s.couleur)}>
                Voir
              </button>
            </div>
          </div>
        ))}

        <button className="btn-save" onClick={handleSave}>
          Enregistrer l'alerte
        </button>
      </div>

      {/* POPUP AJOUT */}
      {popupVisible && (
        <div className="popup-overlay">
          <div className="popup">
            <h3>Ajouter un destinataire</h3>

            <label>Canal :</label>
            <select
              value={selectedChannel}
              onChange={(e) => setSelectedChannel(e.target.value as CanalType)}
              className="sa-select full"
            >
              <option value="sms">SMS</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="email">Email</option>
            </select>

            <input
              value={newDest}
              onChange={(e) => setNewDest(e.target.value)}
              placeholder="Numéro ou email"
              className="sa-input full"
            />

            <button className="btn-add full btn-add-green" onClick={addDestinataire}>
              Ajouter
            </button>

            <button className="full" onClick={() => setPopupVisible(false)}>
              Fermer
            </button>
          </div>
        </div>
      )}

      {/* POPUP LISTE DESTINATAIRES */}
      {popupViewVisible && colorForView && (
        <div className="popup-overlay">
          <div className="popup large">
            <h2>Destinataires – {colorForView}</h2>

            {(["sms", "whatsapp", "email"] as CanalType[]).map((canal) => {
              const list =
                seuils.find((s) => s.couleur === colorForView)?.destinataires[canal] || [];
              if (list.length === 0) return null;

              return (
                <div key={canal} className="dest-block">
                  <h4>{canal.toUpperCase()}</h4>

                  <div className="table-container">
                    <table className="dest-table">
                      <thead>
                        <tr>
                          <th>Valeur</th>
                          <th>Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {list.map((d, index) => (
                          <tr key={index}>
                            <td>{d}</td>
                            <td>
                              <button
                                className="btn-delete"
                                onClick={() => removeDest(colorForView, canal, index)}
                              >
                                Supprimer
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}

            <button className="full" onClick={() => setPopupViewVisible(false)}>
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemeAlerte;